import { Suspense, lazy } from 'react';

import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import type { MarkdownProps } from './Markdown';

/**
 * The one lazy boundary every prose surface imports.
 *
 * `react-markdown` and its GFM plugin are a real chunk, and until now only the file
 * preview paid for it, on demand. The drawer renders markdown in four places; each
 * importing `Markdown` directly would put the chunk on the board's critical path.
 * This wraps it once, so whichever surface asks first loads it and the rest share it.
 */
const Markdown = lazy(() =>
  import('./Markdown').then((module) => ({ default: module.Markdown })),
);

export function MarkdownLazy(props: MarkdownProps) {
  return (
    <Suspense fallback={<MarkdownPlaceholder />}>
      <Markdown {...props} />
    </Suspense>
  );
}

/**
 * Nothing at first, then a quiet line: the chunk usually arrives inside the
 * appearance delay, and a placeholder that flashes for one frame reads as a glitch
 * (spec 079 FR-017).
 */
function MarkdownPlaceholder() {
  const show = usePendingDisplay(true);
  return show ? (
    <p className="text-muted-foreground text-sm" aria-hidden="true">
      …
    </p>
  ) : null;
}
